import React, { useEffect, useRef, useState } from 'react';
import CounterInputLabel from './CounterInputLabel';
import CounterInputMenu from './CounterInputMenu';

import { useAppSelector } from '../../hooks/useRedux';
import { handleClosePopUp } from '../../utils/genericHandles';
import type { CounterInputLabelData, RoomInputData, RoomOpenState } from './types';

const INITIAL_DATA: RoomInputData[] = [
  {
    id: 1,
    adults: 2,
    children: [],
  },
];

interface ICounterInputProps {
  isHome?: boolean;
  hasHeaderCloseButton?: boolean;
  hasHeaderReturnButton?: boolean;
  initialData?: RoomInputData[];
  trigger?: boolean;
  onReturn?: () => void;
  onSubmit: (roomInputData: RoomInputData[], shouldSubmit: boolean) => void;
}

const CounterInput = ({
  isHome = false,
  hasHeaderCloseButton = true,
  hasHeaderReturnButton = false,
  initialData,
  trigger,
  onReturn,
  onSubmit,
  ...props
}: ICounterInputProps) => {
  const { isMobile } = useAppSelector(({ generics }) => ({
    isMobile: generics.isMobile,
  }));

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [initialRoomInputData, setInitialRoomInputData] = useState<RoomInputData[]>(INITIAL_DATA);
  const [initialRoomOpenState, setInitialRoomOpenState] = useState<RoomOpenState[]>(
    initialRoomInputData.map((data, index) => ({
      id: data.id,
      isOpen: index === initialRoomInputData.length - 1,
    })),
  );
  const [roomInputData, setRoomInputData] = useState<RoomInputData[]>(initialRoomInputData);
  const [roomsOpenState, setRoomsOpenState] = useState<RoomOpenState[]>(initialRoomOpenState);
  const [counterInputLabel, setCounterInputLabel] = useState<CounterInputLabelData>({ travelers: 0, rooms: 0 });

  const ref = useRef<HTMLDivElement>(null);

  const handleCloseUndoData = () => {
    setRoomInputData(initialRoomInputData);
    setRoomsOpenState(initialRoomOpenState);
  };

  // helper
  const updateState = (prevState: any, newState: any) => {
    return [...prevState.map((room: any) => (room.id === newState.id ? newState : room))];
  };

  useEffect(
    () =>
      handleClosePopUp(ref, () => {
        setIsMenuOpen(false);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (initialData?.length) {
      setInitialRoomInputData(initialData);
      setRoomInputData(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCounterInputLabel(
      initialRoomInputData.reduce<CounterInputLabelData>(
        (acc, data) => ({ travelers: acc.travelers + data.adults + data.children.length, rooms: acc.rooms + 1 }),
        { travelers: 0, rooms: 0 },
      ),
    );
  }, [initialRoomInputData]);

  useEffect(() => {
    if (trigger !== undefined) {
      setIsMenuOpen(trigger);
    }
  }, [trigger]);

  const handleToggle = () => {
    setIsMenuOpen((prevState) => !prevState);
    if (isMobile) {
      setTimeout(() => handleCloseUndoData(), 200); // wait for menu to disappear to change content
    } else {
      handleCloseUndoData();
    }
  };

  const handleRoomToggle = (roomState: RoomOpenState) => {
    setRoomsOpenState((prevState) => updateState(prevState, roomState) as RoomOpenState[]);
  };

  const handleRoomChange = (roomData: RoomInputData) => {
    setRoomInputData((prevState) => updateState(prevState, roomData) as RoomInputData[]);
  };

  const handleRoomAdd = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setRoomInputData((prevState) => [...prevState, { id: prevState.length + 1, adults: 1, children: [] }]);
    setRoomsOpenState((prevState) => [
      ...prevState.map((room) => ({ ...room, isOpen: false })),
      { id: prevState.length + 1, isOpen: true },
    ]);
  };

  const handleRoomDelete = (roomData: RoomInputData) => {
    setRoomInputData((prevState) => {
      const filteredState = [...prevState.filter((room) => room.id !== roomData.id)];
      return filteredState.map((room, index) => ({ ...room, id: index + 1 }));
    });

    setRoomsOpenState((prevState) =>
      [...prevState.filter((room) => room.id !== roomData.id)].map((room, index) => ({
        id: index + 1,
        isOpen: prevState.length === 2 ? true : room.isOpen, // set first room open if its the only one left
      })),
    );
  };

  const handleChangeValues = () => {
    const output = roomInputData.reduce((acc, roomData) => acc + roomData.adults + roomData.children.length, 0);
    setInitialRoomInputData(roomInputData);
    setInitialRoomOpenState(roomsOpenState);
    setCounterInputLabel({ travelers: output, rooms: roomInputData.length });
  };

  const handleConfirm = () => {
    handleChangeValues();
    setIsMenuOpen(false);
    onSubmit(roomInputData, true);
  };

  useEffect(() => {
    handleChangeValues();
    onSubmit(roomInputData, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomInputData]);

  return (
    <section className="counter-input-container" ref={ref} {...props}>
      <CounterInputLabel labelData={counterInputLabel} isMenuOpen={isMenuOpen} onToggle={handleToggle} />

      <CounterInputMenu
        roomInputData={roomInputData}
        roomsOpenState={roomsOpenState}
        isOpen={isMenuOpen}
        isHome={isHome}
        onRoomToggle={handleRoomToggle}
        onRoomChange={handleRoomChange}
        onRoomDelete={handleRoomDelete}
        onRoomAdd={handleRoomAdd}
        onConfirm={handleConfirm}
        // mobile-only props:
        hasHeaderCloseButton={hasHeaderCloseButton}
        hasHeaderReturnButton={hasHeaderReturnButton}
        onClose={handleToggle}
        onReturn={onReturn}
      />
    </section>
  );
};

export default CounterInput;

export * from './types';
